import { useState } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { Device, useQueryDevices, PhoneSyncApi } from '@frontend/api-phone-sync';
import { UsersApi } from '@frontend/api-users';
import { getUser } from '@frontend/auth-helpers';
import { Trans, useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import {
  emptySectionStyle,
  pageStyle,
  RemoveSyncModal,
  DeviceSection,
  usePhoneSyncControl,
  PhoneSyncUtils,
  PhoneSyncButton,
  PhoneSyncModal,
  usePhonePairSuccessHandler,
  usePhoneSyncMutations,
} from '@frontend/phone-sync';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { SoftphoneStateInterface } from '@frontend/softphone-state';
import { theme } from '@frontend/theme';
import {
  Text,
  Heading,
  useModalControl,
  SearchField,
  useFormField,
  PhoneMissedIcon,
  Tabs,
  SearchIcon,
  ConfirmationModal,
  ModalControlModalProps,
  Modal,
  useAlert,
} from '@frontend/design-system';
import { PhoneSettingsPage } from '../../components/settings/settings-page';

enum PhoneSyncAction {
  Pair = 'Pair',
  Disconnect = 'Disconnect',
  Connect = 'Connect',
}

const NoConnectedDevice = () => {
  const { t } = useTranslation('phoneSync');
  return (
    <section css={emptySectionStyle}>
      <PhoneMissedIcon />
      <Text weight='bold'>{t('No Device Connected')}</Text>
      <Text color='light' style={{ width: theme.spacing(55) }}>
        {t('To connect a device, choose a device from the “Available Devices” list below and click “Connect Device.”')}
      </Text>
    </section>
  );
};

const NoItemSection = () => {
  const { t } = useTranslation('phoneSync');
  return (
    <section css={emptySectionStyle}>
      <SearchIcon />
      <Text weight='bold'>{t('No Items to Display')}</Text>
      <Text color='light' style={{ width: theme.spacing(55) }}>
        {t("You'll find the devices you can connect to here.")}
      </Text>
    </section>
  );
};

export const PhoneSync = ({ hasActiveSoftphoneCall }: { hasActiveSoftphoneCall?: boolean }) => {
  const { t } = useTranslation('phoneSync');
  const removeSyncModal = useModalControl();
  const [controlledTab, setControlledTab] = useState('available-devices');
  const fieldProps = useFormField({ type: 'text' });
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();

  const alerts = useAlert();
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig', 'setPhoneConfig');
  const connectDeviceModalProps = useModalControl();
  const disconnectDeviceModalProps = useModalControl();
  const forgetDeviceModalProps = useModalControl();
  const preventPhoneSyncActionModalProps = useModalControl();
  const [deviceData, setDeviceData] = useState<Device>();

  const { data: devices } = useQueryDevices(selectedLocationIds);
  const { disconnectDevice, forgetDevice, connectDeviceAsync } = usePhoneSyncMutations();
  const { modalProps, buttonProps } = usePhoneSyncControl();

  const connectedDevice = devices?.filter((device) => device.sipProfileId === phoneConfig?.sipProfileId);
  const filteredAllDevices = devices?.filter(
    (device) =>
      device.sipProfileId !== phoneConfig?.sipProfileId &&
      device.name?.toLowerCase().includes(fieldProps.value.toLowerCase())
  );
  const filteredAvailableDevices = filteredAllDevices?.filter((device) => device.availableInContext);

  const [phoneSyncAction, setPhoneSyncAction] = useState<PhoneSyncAction | undefined>(undefined);

  const handlePreventPhoneSyncAction = (actionType: PhoneSyncAction) => {
    setPhoneSyncAction(actionType);
    preventPhoneSyncActionModalProps.openModal();
  };

  const confirmDeviceOverwriteModalProps = useModalControl();
  const { activateCurrentTab } = SoftphoneStateInterface;

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasDeviceConnectionChangeFlag = getFeatureFlagValue('device-connection-change');

  const actuallyConnectDevice = (data: Device) => {
    connectDeviceAsync({ sipProfileId: data.sipProfileId ?? '' }).then(() => {
      activateCurrentTab();
    });
    confirmDeviceOverwriteModalProps.closeModal();
  };

  const initiateConnectDevice = (data: Device) => {
    const user = getUser();

    if (hasDeviceConnectionChangeFlag && !!data.activeUserId && user?.userID !== data.activeUserId) {
      confirmDeviceOverwriteModalProps.openModal();
      return;
    }
    actuallyConnectDevice(data);
  };

  const handleConnectDevice = (data: Device) => {
    if (hasActiveSoftphoneCall) {
      handlePreventPhoneSyncAction(PhoneSyncAction.Connect);
      return;
    }

    setDeviceData(data);
    if (phoneConfig) {
      connectDeviceModalProps.openModal();
    } else if (data?.sipProfileId) {
      initiateConnectDevice(data);
    } else {
      alerts.error(t('Device connection failed'));
    }
  };

  const handleDisconnectDevice = (data: Device) => {
    if (hasActiveSoftphoneCall) {
      handlePreventPhoneSyncAction(PhoneSyncAction.Disconnect);
      return;
    }

    setDeviceData(data);
    disconnectDeviceModalProps.openModal();
  };

  const handleOpenPairDevice = () => {
    if (hasActiveSoftphoneCall) {
      handlePreventPhoneSyncAction(PhoneSyncAction.Pair);
      return;
    }

    buttonProps.triggerProps.onClick();
  };

  const handleForgetDevice = (data: Device) => {
    setDeviceData(data);
    forgetDeviceModalProps.openModal();
  };

  const onSync = usePhonePairSuccessHandler()(selectedLocationIds);
  const user = getUser();

  return (
    <PhoneSettingsPage
      title={t('Device Connection')}
      action={
        <PhoneSyncButton
          {...buttonProps}
          triggerProps={{
            ...buttonProps.triggerProps,
            onClick: handleOpenPairDevice,
          }}
        />
      }
      subtitle={
        <Text>
          {t(
            'Pair and connect your device to take advantage of time-saving features, including click-to-call and personal voicemail.'
          )}
        </Text>
      }
      css={pageStyle}
    >
      <Heading level={3} style={{ marginBottom: theme.spacing(2) }}>
        {t('Connected Device')}
      </Heading>
      <div>
        {!connectedDevice?.length ? (
          <NoConnectedDevice />
        ) : (
          connectedDevice?.map((item, index) => (
            <DeviceSection
              key={item.sipProfileId}
              currentUserId={user?.userID}
              index={index}
              item={item}
              isConnected={true}
              isLocationGroup={PhoneSyncUtils.isLocationGroup(item, accessibleLocationData)}
              handleDisconnectDevice={handleDisconnectDevice}
            />
          ))
        )}
      </div>
      <Heading level={3} style={{ margin: theme.spacing(2, 0) }}>
        {t('Other Devices')}
      </Heading>
      <Tabs controlledTab={controlledTab} onChange={(id) => setControlledTab(id)}>
        <Tabs.Bar css={{ marginBottom: theme.spacing(3), maxWidth: theme.spacing(87) }}>
          <Tabs.Tab id='available-devices' trackingId='phn-portal-deviceConnection-btn-availableDevices'>
            {t('Available Devices')}
          </Tabs.Tab>
          <Tabs.Tab id='all-devices' trackingId='phn-portal-deviceConnection-btn-allDevices'>
            {t('All Devices')}
          </Tabs.Tab>
        </Tabs.Bar>
        <SearchField
          name='search'
          placeholder={controlledTab === 'available-devices' ? t('Search Available Devices') : t('Search All Devices')}
          css={css`
            width: ${theme.spacing(40)};
            margin-bottom: ${theme.spacing(2)};
          `}
          {...fieldProps}
        />
        <Tabs.Panel controller='available-devices' css={{ position: 'relative' }}>
          {!filteredAvailableDevices?.length ? (
            <NoItemSection />
          ) : (
            filteredAvailableDevices.map((item, index) => (
              <DeviceSection
                key={item.sipProfileId}
                index={index}
                item={item}
                currentUserId={user?.userID}
                allowForgetDevice={true}
                handleConnectDevice={handleConnectDevice}
                handleForgetDevice={handleForgetDevice}
                isLocationGroup={PhoneSyncUtils.isLocationGroup(item, accessibleLocationData)}
              />
            ))
          )}
        </Tabs.Panel>
        <Tabs.Panel controller='all-devices'>
          {!filteredAllDevices?.length ? (
            <NoItemSection />
          ) : (
            filteredAllDevices.map((item, index) => (
              <DeviceSection
                key={item.sipProfileId}
                index={index}
                item={item}
                currentUserId={user?.userID}
                allowForgetDevice={true}
                handleConnectDevice={handleConnectDevice}
                handleForgetDevice={handleForgetDevice}
                isLocationGroup={PhoneSyncUtils.isLocationGroup(item, accessibleLocationData)}
              />
            ))
          )}
        </Tabs.Panel>
      </Tabs>
      <RemoveSyncModal removeSyncModal={removeSyncModal} />
      {deviceData && (
        <Modals
          connect={{
            modalProps: connectDeviceModalProps.modalProps,
            action: initiateConnectDevice,
          }}
          disconnect={{
            modalProps: disconnectDeviceModalProps.modalProps,
            action: disconnectDevice,
          }}
          forget={{
            modalProps: forgetDeviceModalProps.modalProps,
            action: forgetDevice,
          }}
          device={deviceData}
        />
      )}
      <PhoneSyncModal
        {...modalProps}
        locationIds={selectedLocationIds}
        onSyncSuccess={onSync}
        syncFn={PhoneSyncApi.pairDevice}
      />
      <PhoneSyncActionErrorModal
        modalProps={preventPhoneSyncActionModalProps.modalProps}
        phoneSyncAction={phoneSyncAction}
      />
      {deviceData && (
        <Modal {...confirmDeviceOverwriteModalProps.modalProps}>
          <ConfirmDeviceOverwriteAccessScreen
            closeModal={() => {
              confirmDeviceOverwriteModalProps.closeModal();
            }}
            device={deviceData}
            connectDevice={actuallyConnectDevice}
          />
        </Modal>
      )}
    </PhoneSettingsPage>
  );
};

const ConfirmDeviceOverwriteAccessScreen = ({
  closeModal,
  device,
  connectDevice,
}: {
  closeModal: () => void;
  device: Device;
  connectDevice: (data: Device) => void;
}) => {
  const { t } = useTranslation('phoneSync');

  const { data } = useQuery({
    queryKey: ['user', device.activeUserId],
    queryFn: () => {
      return UsersApi.list.getUserProfileByUserID(device.activeUserId ?? '');
    },
    enabled: !!device.activeUserId,
  });

  return (
    <>
      <Modal.Header>{t('Overwrite Device Access')}</Modal.Header>
      <Modal.Body>
        {data && (
          <Trans t={t}>
            <Text>
              You are about to take over this device from{' '}
              <Text as='span' weight='bold'>
                {getFullName(data)}
              </Text>
              .
            </Text>
            <Text>
              This will disconnect the user from any call notifications directed at the device. Do you want to proceed?
            </Text>
          </Trans>
        )}
      </Modal.Body>
      <Modal.Footer
        secondary={{
          label: t('Cancel'),
          onClick: () => {
            closeModal();
          },
        }}
        primary={{
          label: t('Proceed'),
          onClick: () => {
            connectDevice(device);
            closeModal();
          },
        }}
      />
    </>
  );
};

const Modals = ({
  connect,
  disconnect,
  forget,
  device,
}: {
  connect: {
    modalProps: ModalControlModalProps;
    action: (device: Device) => void;
  };
  disconnect: {
    modalProps: ModalControlModalProps;
    action: () => void;
  };
  forget: {
    modalProps: ModalControlModalProps;
    action: (payload: { sipProfileId: string }) => void;
  };
  device: Device;
}) => {
  const { t } = useTranslation('phoneSync');

  return (
    <>
      <ConfirmationModal
        {...connect.modalProps}
        onConfirm={() => {
          if (device.sipProfileId) {
            connect.action(device);
          }
        }}
        title={t('Connect Device?')}
        message={t('Connecting {{newDevice}} will disconnect the device that is currently connected.', {
          newDevice: device.name,
        })}
        cancelLabel={t('No, Cancel')}
        confirmLabel={t('Yes, Connect')}
        confirmTrackingId='phn-portal-deviceConnection-btn-connect'
        cancelTrackingId='phn-portal-deviceConnection-btn-cancelConnect'
      />
      <ConfirmationModal
        {...disconnect.modalProps}
        onConfirm={() => disconnect.action()}
        title={t('Disconnect Device?')}
        message={t('Disconnecting this device will prevent you from placing or receiving calls within the Weave app.')}
        cancelLabel={t('No, Cancel')}
        confirmLabel={t('Yes, Disconnect')}
        confirmTrackingId='phn-portal-deviceConnection-btn-disconnect'
        cancelTrackingId='phn-portal-deviceConnection-btn-cancelDisconnect'
      />
      <ConfirmationModal
        {...forget.modalProps}
        onConfirm={() => {
          if (device.sipProfileId) {
            forget.action({ sipProfileId: device?.sipProfileId });
          }
        }}
        title={t('Forget Device?')}
        message={t('Forgetting this devices will remove it from your list of devices.')}
        cancelLabel={t('No Cancel')}
        confirmLabel={t('Yes, Forget')}
        confirmTrackingId='phn-portal-deviceConnection-btn-forget'
        cancelTrackingId='phn-portal-deviceConnection-btn-cancelForget'
      />
    </>
  );
};

const PhoneSyncActionErrorModal = ({
  modalProps,
  phoneSyncAction,
}: {
  modalProps: ModalControlModalProps;
  phoneSyncAction: PhoneSyncAction | undefined;
}) => {
  const { t } = useTranslation('phoneSync');

  const actionString = phoneSyncAction || '';

  return (
    <Modal {...modalProps}>
      <Modal.Header onClose={modalProps.onClose}>
        {t('Cannot {{phoneSyncAction}} Device', { phoneSyncAction: actionString })}
      </Modal.Header>
      <Modal.Body>
        <Text size='medium'>
          {t(
            'You cannot {{phoneSyncAction}} a new device during an active call. Please end the call before proceeding.',
            { phoneSyncAction: actionString.toLowerCase() }
          )}
        </Text>
      </Modal.Body>
      <Modal.Actions
        css={{
          paddingTop: theme.spacing(3),
        }}
        showPrimary={false}
        secondaryLabel='Close'
        onSecondaryClick={modalProps.onClose}
      />
    </Modal>
  );
};
